import type { QueryClient } from '@tanstack/react-query';
import { tryCatch } from '@/try-catch';
import { isAxiosError } from 'axios';
import { api } from '@/api';
import { getChatHydrate } from '@/features/chat/api';
import { citationDocumentId } from '@/features/documents/drawing';
import { citationFromEvent } from '@/features/chat/citations';
import { ChatEventType } from '@/features/chat/types';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { workspaceQuery } from '@/features/workspaces/queries';
import {
  buildEntityRoute,
  isWorkspaceScopedEntity,
  type EntityRouteCtx,
} from '@/features/chat/entity-route';

const entityIdIsWorkspace = (entity: string) =>
  isWorkspaceScopedEntity(entity) ||
  entity === 'tenderPackage' ||
  entity === 'tender' ||
  entity === 'tender_submission_requirements' ||
  entity === 'contract' ||
  entity === 'bid_comparison' ||
  entity === 'bid_draft';

export const resolveGoRedirect = async (
  queryClient: QueryClient,
  entity: string,
  entityId: string,
  search: { evalId?: string; page?: number; threadId?: string },
) => {
  if (entity === 'teams-link') return null;
  if (entity === 'project')
    return {
      to: '/project/$projectId' as const,
      params: { projectId: entityId },
      search: { threadId: search.threadId },
    };

  const workspaceId = search.evalId ?? (entityIdIsWorkspace(entity) ? entityId : undefined);
  if (!workspaceId) return null;

  try {
    const workspace = await queryClient.query(workspaceQuery(workspaceId));
    if (!workspace.projectId) return null;
    const documentType = workspace.type;
    const ctx: EntityRouteCtx = {
      projectId: workspace.projectId,
      documentType,
      parentResourceId: workspace.id,
      workspaceId: workspace.id,
    };
    const routeEntityId = isWorkspaceScopedEntity(entity) ? workspace.id : entityId;
    const routeEntity =
      entity === 'strengths_weaknesses' && entityId === workspace.id ? documentType : entity;
    const target =
      buildEntityRoute(routeEntity, routeEntityId, ctx) ??
      buildEntityRoute(documentType, workspace.id, ctx);
    if (!target) return null;

    return {
      ...target,
      search: {
        ...('search' in target && typeof target.search === 'object' ? target.search : {}),
        page: search.page,
        threadId: search.threadId,
      },
    };
  } catch (error) {
    if (isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404))
      return null;
    throw error;
  }
};

export const resolveGoCitation = async (
  queryClient: QueryClient,
  documentId: string,
  search: {
    evalId?: string;
    projectId?: string;
    threadId?: string;
    messageId?: string;
    citation?: number;
    channelSource?: string;
  },
) => {
  if (search.channelSource) {
    const ordinal = search.citation;
    if (ordinal === undefined || !Number.isInteger(ordinal) || ordinal < 1) return null;
    const { data: response, error } = await tryCatch(
      api.get<{ projectId: string; citation: Record<string, unknown> }>(
        `v1/integrations/teams/channel-sources/${search.channelSource}/${ordinal}`,
      ),
    );
    if (isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) return null;
    if (error) throw error;
    const citation = citationFromEvent(response.data.citation);
    return citationDocumentId(citation) === documentId
      ? { ...citation, projectId: response.data.projectId }
      : null;
  }
  const scopeId = search.projectId ?? search.evalId;
  if (
    !scopeId ||
    !!search.projectId === !!search.evalId ||
    !search.threadId ||
    !search.messageId ||
    !Number.isInteger(search.citation) ||
    (search.citation ?? 0) < 1
  )
    return null;
  try {
    let projectId = search.projectId;
    let resourceType: Parameters<typeof getChatHydrate>[2] = DOCUMENT_TYPE.project;
    if (search.evalId) {
      const workspace = await queryClient.query(workspaceQuery(search.evalId));
      resourceType = DOCUMENT_TYPE[workspace.type];
      projectId = workspace.projectId;
    }
    if (!projectId) return null;
    const { data: hydrated } = await getChatHydrate(search.threadId, scopeId, resourceType);
    const message = hydrated.messages.find((item) => item.id === search.messageId);
    const citations = message?.events.filter(
      (event) => event.eventType === ChatEventType.CITATIONS,
    );
    const event = citations?.[(search.citation ?? 0) - 1];
    if (!event) return null;
    const citation = citationFromEvent(event.payload);
    return citationDocumentId(citation) === documentId ? { ...citation, projectId } : null;
  } catch (error) {
    if (isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) return null;
    throw error;
  }
};
